import { FC, useState, useEffect } from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
  Grid,
  FormControl,
  TextField,
  FormControlLabel,
  Switch,
} from "@material-ui/core";

import {
  OpenEdgePaymentData,
  OpenEdgePaymentDataErrorProps,
  ErrorProps,
} from "../../../entities/PaymentOptions";
import ReactNotification, {
  SUCCESS,
  ACH_DISABLED_MESSAGE,
  CC_DISABLED_MESSAGE,
  DANGER,
  ACH_ENABLED_MESSAGE,
  CC_ENABLED_MESSAGE,
} from "../../../utils/ReactNotification.config";
import {
  check,
  objError,
  X_WEB_ID,
  TERMINAL_ID,
  AUTH_KEY,
  X_WEB_ID_MANDATORY,
  TERMINAL_ID_MANDATORY,
  AUTH_KEY_MANDATORY,
  DETAILS_INVALID,
  ACH,
  ACH_TITLE,
  CC_TITLE,
  CC_LABEL,
} from "./paymentMethods.const";
import { useStylesInput } from "./style";
import { ACH_PAYMENT, CC_PAYMENT } from "../../../utils/api.config.consts";
import { api, base_url } from "../../../utils/env";
import { PaymentMode } from "../../../constants/payment.const";

interface OpenEdgeMethodsProps {
  type: string;
  companyID: string | null;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  data: any;
  masterID: string | null;
}

const OpenEdgeSelection: FC<OpenEdgeMethodsProps> = ({
  type,
  companyID,
  setLoading,
  data,
  masterID,
}: OpenEdgeMethodsProps) => {
  const [paymentData, setPaymentData] = useState<OpenEdgePaymentData>(check);
  const [errors, setErrors] = useState<OpenEdgePaymentDataErrorProps>(objError);
  const classesInput = useStylesInput();
  const [selectCompanyID, setSelectCompanyID] = useState<string | null>("");

  useEffect(() => {
    if (companyID && companyID !== "default") {
      setPaymentData({
        ...paymentData,
        ...check,
        ...data,
        masterId: Number(masterID),
        companyId: companyID,
        xWebId: data && data.xwebId ? data.xwebId : "",
        authKey:
          data && data.enable && data.enable === true ? data.authKey : "",
      });
    } else {
      setPaymentData({
        ...paymentData,
        ...check,
        masterId: Number(masterID),
        companyId: companyID,
      });
    }
    setErrors(objError);
  }, [companyID, data, masterID]);

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    inputName: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    validation(inputName, event);
    setPaymentData({
      ...paymentData,
      ...{ [inputName]: event.currentTarget.value },
    });
  };

  const setError = (
    property: string = "",
    status: boolean = false,
    message: string = ""
  ) => {
    setErrors({
      ...errors,
      [property]: {
        status: status,
        message: message,
      },
    });
  };

  const validation = (
    property: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget.value.trim();

    if (property === X_WEB_ID) {
      if (!value) setError(property, true, X_WEB_ID_MANDATORY);
      else setError(property);
    }

    if (property === TERMINAL_ID) {
      if (!value) setError(property, true, TERMINAL_ID_MANDATORY);
      else setError(property);
    }

    if (property === AUTH_KEY) {
      if (!value) setError(property, true, AUTH_KEY_MANDATORY);
      else setError(property);
    }
  };

  const onConfirmValidation = () => {
    let xWebIdError: ErrorProps;
    let terminalIdError: ErrorProps;
    let authKeyError: ErrorProps;
    let valid: boolean = false;

    if (!paymentData.xWebId) {
      xWebIdError = {
        status: true,
        message: X_WEB_ID_MANDATORY,
      };
      valid = true;
    } else {
      xWebIdError = {
        status: false,
        message: "",
      };
    }

    if (!paymentData.terminalId) {
      terminalIdError = {
        status: true,
        message: TERMINAL_ID_MANDATORY,
      };
      valid = true;
    } else {
      terminalIdError = {
        status: false,
        message: "",
      };
    }

    if (!paymentData.authKey) {
      authKeyError = {
        status: true,
        message: AUTH_KEY_MANDATORY,
      };
      valid = true;
    } else {
      authKeyError = {
        status: false,
        message: "",
      };
    }

    setErrors({
      xWebId: xWebIdError,
      terminalId: terminalIdError,
      authKey: authKeyError,
      webToken: xWebIdError,
      merchantId: terminalIdError,
      apiKey: authKeyError,
    });
    return valid;
  };

  const updatePaymentDataStatus = (status: boolean) => {
    setPaymentData({
      ...paymentData,
      ...{ enable: status },
      ...{ staxEnable: false },
    });
  };

  const updatePaymentDataStatusAndAuthKey = (
    status: boolean,
    newAuthKey: string
  ) => {
    setPaymentData({
      ...paymentData,
      ...{
        enable: status,
        authKey: newAuthKey,
      },
    });
  };

  const savePaymentOptions = async (statusCode: boolean) => {
    setLoading(true);
    const option = type === ACH ? ACH_PAYMENT : CC_PAYMENT;
    await axios
      .post(`${base_url}${api}/${option}`, {
        companyId: paymentData.companyId?.toString().trim(),
        masterId: masterID,
        enable: statusCode,
        authKey: paymentData.authKey?.toString().trim(),
        terminalId: paymentData.terminalId?.toString().trim(),
        xwebId: paymentData.xWebId?.toString().trim(),
        termsAndConditions: "",
        paymentGateway: PaymentMode.OPEN_EDGE,
      })
      .then((response: AxiosResponse<any>) => {
        const TITLE: string = type === ACH ? ACH_TITLE : CC_TITLE;
        let message: string;
        if (response.data?.enable) {
          message = type === ACH ? ACH_ENABLED_MESSAGE : CC_ENABLED_MESSAGE;
          updatePaymentDataStatusAndAuthKey(
            response.data?.enable,
            response.data?.authKey
          );
        } else {
          message = type === ACH ? ACH_DISABLED_MESSAGE : CC_DISABLED_MESSAGE;
          updatePaymentDataStatusAndAuthKey(false, "");
        }
        setLoading(false);
        ReactNotification.showNotification(TITLE, message, SUCCESS);
        window.location.replace(`/admin/successwarepay/`);
      })
      .catch((error: AxiosError<any>) => {
        setLoading(false);
        const TITLE: string = type === ACH ? ACH_TITLE : CC_TITLE;
        ReactNotification.showNotification(TITLE, DETAILS_INVALID, DANGER);
        if (statusCode) {
          updatePaymentDataStatusAndAuthKey(!statusCode, "");
        } else {
          updatePaymentDataStatus(!statusCode);
        }
      });
  };

  const onConfirmAction = (status: boolean) => {
    onConfirmValidation()
      ? updatePaymentDataStatus(status)
      : savePaymentOptions(!status);
  };

  const checkDisable = (): boolean => {
    return !paymentData.enable &&
      companyID &&
      companyID.trim() !== "" &&
      companyID !== "default"
      ? false
      : true;
  };
  return (
    <section className=" box-shadow custom-card-enable-success-pay">
      <Grid container direction="row">
        <span className="payment-options-title-label">
          {type === ACH ? ACH_TITLE : CC_LABEL} Settings
        </span>
      </Grid>
      <div className="grid-margin">
        <Grid container direction="row" md={6} xs={12}>
          <div className="required-star">
            <FormControl
              required
              className={classesInput.formControl}
              size="small"
            >
              <TextField
                id={`x-web-${type}`}
                label="XWebID"
                name={`xWebId-${type}`}
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ className: classesInput.input }}
                error={errors.xWebId.status}
                value={paymentData.xWebId ? paymentData.xWebId : ""}
                helperText={errors.xWebId.message}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => onInputChange(event, X_WEB_ID)}
                onBlur={(event: any) => validation(X_WEB_ID, event)}
                disabled={checkDisable()}
              />
            </FormControl>
            <div className={`required-default`}>*</div>
          </div>
        </Grid>
        <Grid container direction="row" md={6} xs={12}>
          <div className="required-star">
            <FormControl
              required
              className={classesInput.formControl}
              size="small"
            >
              <TextField
                id={`terminal-${type}`}
                label="Terminal ID"
                name={`terminalId-${type}`}
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ className: classesInput.input }}
                error={errors.terminalId.status}
                value={paymentData.terminalId ? paymentData.terminalId : ""}
                helperText={errors.terminalId.message}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => onInputChange(event, TERMINAL_ID)}
                onBlur={(event: any) => validation(TERMINAL_ID, event)}
                disabled={checkDisable()}
              />
            </FormControl>
            <div className={`required-default`}>*</div>
          </div>
        </Grid>
        <Grid container md={12}>
          <Grid container direction="row" md={6} xs={12}>
            <div className="required-star">
              <FormControl
                required
                className={classesInput.formControl}
                size="small"
              >
                <TextField
                  id={`auth-key-${type}`}
                  label="Auth Key"
                  name={`authKey-${type}`}
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  inputProps={{ className: classesInput.input }}
                  error={errors.authKey.status}
                  value={paymentData.authKey ? paymentData.authKey : ""}
                  helperText={errors.authKey.message}
                  onChange={(
                    event: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >
                  ) => onInputChange(event, AUTH_KEY)}
                  onBlur={(event: any) => validation(AUTH_KEY, event)}
                  disabled={checkDisable()}
                />
              </FormControl>
              <div className={`required-default`}>*</div>
            </div>
          </Grid>
          <Grid
            container
            direction="row"
            md={6}
            xs={12}
            alignItems="center"
            justifyContent="center"
          >
            <FormControlLabel
              className={classesInput.switch}
              classes={{ label: classesInput.switchLabel }}
              control={
                <Switch
                  className="custom-switch"
                  disabled={
                    companyID &&
                    companyID.trim() !== "" &&
                    companyID !== "default"
                      ? false
                      : true
                  }
                  checked={paymentData.enable}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onConfirmAction(paymentData.enable)
                  }
                  name="checkedA-switch"
                />
              }
              label={`Enable ${type === ACH ? ACH_TITLE : CC_LABEL} Access`}
            />
          </Grid>
        </Grid>
      </div>
    </section>
  );
};

export default OpenEdgeSelection;
